import { FC, useState } from 'react';

import { format, parseISO } from 'date-fns';
import { es, ptBR } from 'date-fns/locale';

import {
  Box,
  Button,
  IconButton,
  Input,
  Popover,
  Text
} from '@nimbus-ds/components';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@nimbus-ds/icons';

import { DateQueryFormat } from 'commons/utils/constants';

export interface MonthPickerProps {
  value?: string;
  language: string;
  onChange: (value: string) => void;
  onClick?: () => void;
  placeholder?: string;
}

const FROM_YEAR_OFFSET = 5;
const MONTHS_IN_YEAR = 12;

const MonthPicker: FC<MonthPickerProps> = ({
  value,
  language,
  onChange,
  onClick,
  placeholder
}) => {
  const [open, setOpen] = useState(false);

  const locale = language === 'pt-BR' ? ptBR : es;
  const today = new Date();
  const selected = value ? parseISO(value) : undefined;

  const capitalize = (text: string) =>
    text.replace(/^\w/, (char) => char.toUpperCase());

  const label = selected
    ? capitalize(format(selected, 'LLLL yyyy', { locale }))
    : '';

  const [viewYear, setViewYear] = useState((selected ?? today).getFullYear());

  const fromYear = today.getFullYear() - FROM_YEAR_OFFSET;
  const toYear = today.getFullYear();

  const months = Array.from({ length: MONTHS_IN_YEAR }, (_, month) => {
    const date = new Date(viewYear, month, 1);

    return {
      month,
      label: capitalize(format(date, 'LLL', { locale })),
      isSelected:
        !!selected &&
        selected.getFullYear() === viewYear &&
        selected.getMonth() === month,
      isDisabled:
        viewYear > today.getFullYear() ||
        (viewYear === today.getFullYear() && month > today.getMonth())
    };
  });

  const handleSelect = (month: number) => {
    onChange(format(new Date(viewYear, month, 1), DateQueryFormat.Current));
    setOpen(false);
  };

  return (
    <Popover
      visible={open}
      onVisibility={setOpen}
      position="bottom-start"
      arrow={false}
      content={
        <Box
          display="flex"
          flexDirection="column"
          gap="4"
          width="260px"
          padding="4"
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <IconButton
              source={<ChevronLeftIcon />}
              backgroundColor="transparent"
              borderColor="transparent"
              onClick={() => setViewYear((year) => year - 1)}
              disabled={viewYear <= fromYear}
            />

            <Text fontWeight="medium" fontSize="highlight">
              {viewYear}
            </Text>

            <IconButton
              source={<ChevronRightIcon />}
              backgroundColor="transparent"
              borderColor="transparent"
              onClick={() => setViewYear((year) => year + 1)}
              disabled={viewYear >= toYear}
            />
          </Box>

          <Box
            display="grid"
            gridTemplateColumns="repeat(3, auto)"
            justifyContent="space-between"
            gap="2"
            role="grid"
          >
            {months.map(({ month, label, isSelected, isDisabled }) => (
              <Button
                key={month}
                appearance={isSelected ? 'primary' : 'neutral'}
                disabled={isDisabled}
                onClick={() => handleSelect(month)}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>
      }
    >
      <Input
        readOnly
        value={label}
        placeholder={placeholder}
        append={<CalendarDaysIcon />}
        appendPosition="end"
        onClick={() => {
          setOpen(true);
          onClick?.();
        }}
      />
    </Popover>
  );
};

export default MonthPicker;
